import { AlertTriangle, BookClosed, Columns03, Grid01, List, Plus, SearchLg, Upload01 } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BulkActionBar from 'src/components/BulkActionBar';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DeleteModal from 'src/components/DeleteModal';
import DraggableLayout from 'src/components/DraggableLayout';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TabButton from 'src/components/TabButton';
import TableCmp from 'src/components/TableCmp';
import TableTabButton from 'src/components/TableTabButton';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setConsigneeOptions, setConsignees, setShipperOptions, setShippers } from 'src/redux/address.slice';
import { setAddressColumns } from 'src/redux/basic.slice';
import { RootState } from 'src/redux/store';
import { createAddress, deleteAddress, getAddressList, updateAddress, updateAddressTableColumnSettings } from 'src/services/AddressBookServices';
import { getDuplicateAddressMessage } from 'src/utils/addressBookDuplicate';
import { getCountryFlag, getSelectBoxOptions } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { AddressType, addressTypes, CP_ADDRESS_TABLE_ALIAS, IAddressForm, IAddressListRow } from './addressBook.const';
import AddressBulkActionBar from './AddressBulkActionBar';
import AddressDraggableCell from './AddressDraggableCell';
import AddressGridView from './AddressGridView';
import AddressListRow from './AddressListRow';
import AddressLoaderRow from './AddressLoaderRow';
import AddAddressModal from './Modals/AddAddressModal';
import ImportAddressModal from './Modals/ImportAddressModal';

const initParams = {
    search: '',
    sortType: 'desc',
    sortField: 'createdAt',
    page: 1,
    limit: 25,
    addressType: null as number | null,
};

const recordsPerPageArray = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

// Fluid sizing following the WALHQ table pattern: text columns truncate but stretch
// (`max-w-px`), fixed-content columns cap at a viewport percentage so the table fills the screen.
const ADDRESS_COLUMN_CLASSNAME_MAP: Record<string, string> = {
    companyName: 'max-w-px min-w-[170px]',
    addressType: 'max-w-[9.15%] min-w-[140px]',
    fullAddress: 'max-w-px min-w-[270px]',
    contactName: 'max-w-px min-w-[170px]',
    contactNumber: 'max-w-[9.15%] min-w-[170px]',
    additionalServices: 'max-w-[9.15%] min-w-[160px]',
};

const ADDRESS_COLUMN_TRANSLATION_MAP: Record<string, string> = {
    companyName: 'AddressBook.Name',
    addressType: 'AddressBook.Address_Type',
    fullAddress: 'AddressBook.Address',
    contactName: 'AddressBook.Contact_Name',
    contactNumber: 'AddressBook.Phone_Number',
    additionalServices: 'AddressBook.Additional_Services',
};

const SORTABLE_INITIAL_STATE = { isOpen: false, data: [] as any[] };

const AddressBook = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { shippers, consignees } = useSelector((state: RootState) => state.addresses);
    const { additionalServices } = useAddressDetails();

    const [isShowAddAddressModal, setIsShowAddAddressModal] = useState(false);
    const [isShowImportModal, setIsShowImportModal] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
    const [action, setAction] = useState<{ mode: string | null; team: boolean }>({
        mode: null,
        team: false,
    });
    const [team, setTeam] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [total, setTotal] = useState(0);
    const [addresses, setAddresses] = useState<IAddressListRow[]>([]);
    const [params, setParams] = useState(initParams);
    const [editAddressData, setEditAddressData] = useState<IAddressForm | null>(null);
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

    // Tab states
    const [tabsData, setTabsData] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<string | number>('All');
    const [isTabsLoading, setIsTabsLoading] = useState(false);

    // Column sort states
    const addressColumns = useSelector((state: RootState) => state.basic.addressColumns);
    const [isRowSortable, setIsRowSortable] = useState(SORTABLE_INITIAL_STATE);
    const [columnsOrder, setColumnsOrder] = useState<any[]>([]);

    // Bulk selection state
    const [bulkSelection, setBulkSelection] = useState({ isOpen: false, data: [] as string[] });
    const [showAllSelected, setShowAllSelected] = useState(false);

    const selectedAddressIds = bulkSelection.data;

    const displayedAddresses = useMemo(
        () => (showAllSelected ? addresses.filter((a: IAddressListRow) => selectedAddressIds.includes(a.id)) : addresses),
        [showAllSelected, addresses, selectedAddressIds]
    );

    const setSelectedAddressIds = useCallback((ids: string[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectAddress = useCallback((id: string) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const getAdditionalServices = useCallback(
        (address: any) => [
            ...(address.pickupAdditionalService || []).map((serviceId: any) => ({
                id: serviceId,
                label: additionalServices?.pickup?.find((service) => service.id === Number(serviceId))?.name || '',
                type: 'PICKUP' as const,
            })),
            ...(address.deliveryAdditionalService || []).map((serviceId: any) => ({
                id: serviceId,
                label: additionalServices?.delivery?.find((service) => service.id === Number(serviceId))?.name || '',
                type: 'DELIVERY' as const,
            })),
        ],
        [additionalServices]
    );

    const getAddressTypes = useCallback(
        (address: any) =>
            address?.addressType?.toLowerCase() === 'both'
                ? addressTypes.filter((type: any) => type?.type === addressTypes[0]?.type.toLowerCase() || type?.type === addressTypes[1]?.type.toLowerCase())
                : addressTypes.filter((type: any) => type?.type === address?.addressType?.toLowerCase()),
        []
    );

    const formatAddressRowData = useCallback(
        (rowData: any[]) => {
            if (!rowData.length) return [];
            const first = rowData[0];
            const services = getAdditionalServices(first);
            const types = getAddressTypes(first);

            return [
                [
                    { id: 'companyName', name: 'Name', data: first?.companyName || '-', boxWrapClassName: 'max-w-[200px]' },
                    { id: 'addressType', name: 'Type', data: types, boxWrapClassName: 'max-w-[200px]' },
                    { id: 'fullAddress', name: 'Address', data: first?.fullAddress || '-', boxWrapClassName: 'max-w-[260px]' },
                    { id: 'contactName', name: 'Contact Name', data: first?.contactName || '-', boxWrapClassName: 'max-w-[180px]' },
                    { id: 'contactNumber', name: 'Phone Number', data: first?.contactNumber || '-', boxWrapClassName: 'max-w-[200px]' },
                    {
                        id: 'additionalServices',
                        name: 'Additional Services',
                        data: {
                            count: services.length,
                            tooltip: services
                                .map((s) => s.label)
                                .filter(Boolean)
                                .join(', '),
                        },
                        boxWrapClassName: 'max-w-[200px]',
                    },
                ],
            ];
        },
        [getAdditionalServices, getAddressTypes]
    );

    const headCells = useMemo(() => {
        const sorted = [...addressColumns].sort((a, b) => a.displayOrder - b.displayOrder);

        // Map configurable columns
        const mappedColumns = sorted.map((column) => ({
            ...column,
            sortable: column.id !== 'additionalServices' && addresses.length > 0 && !isLoading,
            rowClassName: ADDRESS_COLUMN_CLASSNAME_MAP[column.id] || '',
            name: t(ADDRESS_COLUMN_TRANSLATION_MAP[column.id] as any) || column.name,
        }));

        // Action column is always last and never part of user-configurable columns
        const actionColumn = {
            id: 'action',
            name: '',
            visible: true,
            sortable: false,
            rowClassName: 'w-[64px] max-w-[64px] min-w-[64px] sticky right-0 bg-inherit',
            displayOrder: sorted.length + 1,
        };

        const checkboxCell = {
            id: 'checkbox',
            name: (
                <CheckBox
                    id="selectAllAddresses"
                    checked={false}
                    onChangeFunc={() => setSelectedAddressIds(addresses.map((a: IAddressListRow) => a.id))}
                    name="selectAllAddresses"
                    value="selectAllAddresses"
                    disabled={isLoading}
                />
            ),
            sortable: false,
            visible: true,
            rowClassName: 'w-[40px] min-w-[40px] max-w-[40px] !pr-2 sticky left-0 bg-inherit',
        };

        return [checkboxCell, ...mappedColumns, actionColumn];
    }, [addressColumns, addresses, isLoading, t, setSelectedAddressIds]);

    const hasColumnChanges = useMemo(() => {
        if (!columnsOrder.length) return false;

        return columnsOrder.some((col: any) => {
            const original = addressColumns.find((c) => c.id === col.id);
            if (!original) return true;

            return col.displayOrder !== original.displayOrder || col.visible !== original.visible;
        });
    }, [columnsOrder, addressColumns]);

    const fetchAddresses = async () => {
        setAddresses([]);
        setIsLoading(true);

        try {
            const apiParams = { ...params };

            if (params.addressType !== null) {
                apiParams.addressType = params.addressType;
            }

            const response = await getAddressList(apiParams);

            if (response?.status) {
                const data = response?.data?.addressBooks?.map((i: any) => ({
                    ...i,
                    pickupAdditionalService: i?.pickupAdditionalService ? i?.pickupAdditionalService : [],
                    deliveryAdditionalService: i?.deliveryAdditionalService ? i?.deliveryAdditionalService : [],
                }));
                setAddresses(data || []);
                setTotal(response?.data?.pagination?.total ?? 0);
            }
        } catch (error) {
            console.error('Error fetching addresses:', error);
            WalToast.error(t('AddressBook.Error_Fetching_Addresses'));
            setAddresses([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = useCallback(
        (addressId: string) => {
            const addressToEdit = addresses.find((addr) => addr.id === addressId);

            if (addressToEdit) {
                const formData: any = {
                    ...addressToEdit,
                    addressType: addressToEdit.addressType?.toLowerCase() as AddressType,
                    pickupAdditionalService: addressToEdit.pickupAdditionalService?.map((i) => Number(i)) || [],
                    deliveryAdditionalService: addressToEdit.deliveryAdditionalService?.map((i) => Number(i)) || [],
                    address: addressToEdit?.fullAddress,
                    contact: addressToEdit?.contactNumber,
                };

                setEditAddressData(formData);
                setIsShowAddAddressModal(true);
            }
        },
        [addresses]
    );

    const handleActionType = useCallback(
        (keyAction: string, mode: string | null = null, address: any = {}) =>
            () => {
                setTeam(address);
                setAction((old) => ({ ...old, [keyAction]: true, mode }));
            },
        []
    );

    const handleModalAction = useCallback(
        (status: boolean) => async () => {
            if (status) {
                setIsDeleteLoading(true);

                try {
                    const response = await deleteAddress(team?.id);

                    if (response?.status) {
                        setAddresses((prev: IAddressListRow[]) => prev.filter((i) => i.id !== team?.id));

                        const addressType = team?.addressType?.toLowerCase();
                        const isShipperType = addressType === 'shipper' || addressType === 'both';
                        const isConsigneeType = addressType === 'consignee' || addressType === 'both';

                        if (isShipperType) {
                            const updatedShippers = shippers.filter((s: any) => s.id !== team?.id);
                            dispatch(setShippers(updatedShippers));
                            dispatch(setShipperOptions(getSelectBoxOptions(updatedShippers.slice(0, 5), 'id', 'companyName')));
                        }

                        if (isConsigneeType) {
                            const updatedConsignees = consignees.filter((c: any) => c.id !== team?.id && c.id !== team?.consigneeId);
                            dispatch(setConsignees(updatedConsignees));
                            dispatch(setConsigneeOptions(getSelectBoxOptions(updatedConsignees.slice(0, 5), 'id', 'companyName')));
                        }

                        WalToast.success(t('AddressBook.Address_deleted'));
                    }
                } catch (error: any) {
                    console.error('Error deleting address:', error);
                    WalToast.error(error?.response?.data?.message ?? t('AddressBook.Address_delete_failed'));
                }
                setIsDeleteLoading(false);
            }
            setAction({ mode: null, team: false });
            setTeam({});
        },
        [team, t]
    );

    const handleStoreShipperConsignee = (newAddress: any, isUpdate: boolean = false) => {
        const addressType = newAddress?.addressType?.toLowerCase();
        const isShipper = addressType === 'shipper' || addressType === 'both';
        const isConsignee = addressType === 'consignee' || addressType === 'both';

        let updatedShippers = [...shippers];
        let updatedConsignees = [...consignees];

        if (isUpdate) {
            if (isShipper) {
                updatedShippers = updatedShippers.map((s) => (s.id === newAddress.id ? { ...s, ...newAddress } : s));
            }

            if (isConsignee) {
                updatedConsignees = updatedConsignees.map((c) => (c.id === newAddress.id ? { ...c, ...newAddress } : c));
            }
        } else {
            if (isShipper) {
                updatedShippers = [newAddress, ...updatedShippers];
            }

            if (isConsignee) {
                const updatedConsigneesData = { ...newAddress, id: newAddress?.consigneeId };
                updatedConsignees = [updatedConsigneesData, ...updatedConsignees];
            }
        }

        dispatch(setShippers(updatedShippers));
        dispatch(setConsignees(updatedConsignees));

        const filteredShipperOptionsValue = getSelectBoxOptions(updatedShippers.slice(0, 5), 'id', 'companyName');
        const filteredConsigneeOptionsValue = getSelectBoxOptions(updatedConsignees.slice(0, 5), 'id', 'companyName');

        dispatch(setShipperOptions(filteredShipperOptionsValue));
        dispatch(setConsigneeOptions(filteredConsigneeOptionsValue));
    };

    const handleAddressModalSubmit = async (data: IAddressForm, setError: (field: string, error: { type: string; message: string }) => void) => {
        const payload = {
            ...data,
            name: data?.companyName,
            contactNumber: `+${data?.contact}`,
            pickupAdditionalService: data?.pickupAdditionalService,
            deliveryAdditionalService: data?.deliveryAdditionalService,
            addressType: data?.addressType?.toUpperCase(),
            businessHoursStartTime: data?.businessHoursStartTime || null,
            businessHoursEndTime: data?.businessHoursEndTime || null,
        };

        try {
            if (editAddressData?.id) {
                const res = await updateAddress({ ...payload, id: editAddressData.id });

                if (res?.status && res?.data?.id) {
                    const newData = { ...res?.data };
                    setAddresses((prev) => {
                        if (prev.some((i) => i?.id == newData?.id)) {
                            return prev.map((i) => (i?.id == newData?.id ? newData : i));
                        }

                        return prev.map((i) => (i?.id == editAddressData?.id ? newData : i));
                    });

                    handleStoreShipperConsignee(newData, true);
                    WalToast.success(t('AddressBook.Address_updated'));
                    setIsShowAddAddressModal(false);
                    setEditAddressData(null);
                }
            } else {
                const dataResponse = await createAddress(payload);

                if (dataResponse?.status && dataResponse?.data?.id) {
                    setAddresses((prev) => [{ ...dataResponse.data }, ...prev]);
                    handleStoreShipperConsignee(dataResponse.data, false);
                    WalToast.success(t('AddressBook.Address_created'));
                    setIsShowAddAddressModal(false);
                    setEditAddressData(null);
                }
            }
        } catch (error: any) {
            console.error('Error saving address:', error);

            const duplicateMessage = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

            if (duplicateMessage) {
                // Shown on the name field's alert icon, not as a toast (client, 2026-10-09): name +
                // address together are what clashed, and the name is the one the user can change
                // without re-picking a place.
                setError('companyName', { type: 'server', message: duplicateMessage });
            } else if (error?.response?.data?.errorMessages) {
                error.response.data.errorMessages.forEach((err: any) => {
                    const fieldMap: { [key: string]: string } = {
                        name: 'companyName',
                        contactNumber: 'contact',
                        stateCode: 'address',
                        countryCode: 'address',
                        streetNumber: 'address',
                        latitude: 'address',
                        longitude: 'address',
                        city: 'address',
                        state: 'address',
                        country: 'address',
                        postal: 'address',
                        fullAddress: 'address',
                    };

                    const mappedField = fieldMap[err.field] || err.field;
                    const errorType = mappedField === 'address' ? 'manualWithTooltip' : 'server';

                    setError(mappedField, { type: errorType, message: err.message });
                });
            } else {
                WalToast.error(t('AddressBook.Address_save_failed'));
            }
        }
    };

    const handleModalClose = useCallback(() => {
        setIsShowAddAddressModal(false);
        setEditAddressData(null);
    }, []);

    const searchDebounce = useCallback(
        debounce((debSearch: string) => setParams((old: any) => ({ ...old, search: debSearch, page: 1 })), 700),
        []
    );

    const handleFilter = (event: any) => {
        const { value } = event.target;
        setSearch(value);
        setIsLoading(true);
        searchDebounce(value);
    };

    const handleTabClick = (tab: any) => () => {
        if (tab?.value === activeTab) return;

        setActiveTab(tab?.value);
        setSearch('');

        let addressType: number | null = null;

        if (tab.value === 'shipper') addressType = 1;
        else if (tab.value === 'consignee') addressType = 2;

        setParams({ ...initParams, addressType });
    };

    const handleSortable =
        (isOpen: boolean = true) =>
        () => {
            if (isOpen) {
                const formattedData = formatAddressRowData(addresses.length ? addresses : []);

                if (formattedData.length > 0) {
                    const sorted = addressColumns
                        .map((column) => {
                            const found = formattedData[0].find((f: any) => f.id === column.id);

                            return found ? { ...found, visible: column.visible } : null;
                        })
                        .filter(Boolean);
                    setIsRowSortable({ isOpen: true, data: [sorted] });
                }
            } else {
                setIsRowSortable(SORTABLE_INITIAL_STATE);
                setColumnsOrder([]);
            }
        };

    const handleColumnChange = (columns: any) => {
        setColumnsOrder(columns);
    };

    const handleColumnOrderSubmit = () => {
        const cleaned = columnsOrder.map((col: any) => {
            // eslint-disable-next-line no-unused-vars
            const { data: data, boxWrapClassName: boxWrapClassName, ...rest } = col;

            return rest;
        });
        dispatch(setAddressColumns(cleaned));
        setIsRowSortable(SORTABLE_INITIAL_STATE);
        setColumnsOrder([]);

        updateAddressTableColumnSettings({
            tableAlias: CP_ADDRESS_TABLE_ALIAS,
            columnSettings: cleaned,
        }).catch(() => {
            // Silent fail — local state already updated
        });
    };

    useEffect(() => {
        setSearch(params.search || '');
    }, [params.search]);

    useEffect(() => {
        if (tabsData.length === 0) {
            setIsTabsLoading(true);
            setTabsData([
                { value: 'All', name: 'All', count: 0 },
                { value: 'shipper', name: 'Shipper', count: 0 },
                { value: 'consignee', name: 'Consignee', count: 0 },
            ]);
            setIsTabsLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchAddresses();

        return () => {
            controller.abort();
        };
    }, [params]);

    return (
        <PageSectionLayout
            header={
                <Header
                    title={t('AddressBook.Address_Book')}
                    Icon={BookClosed}
                    isShowNotificationBox
                    leftClassName="!w-auto"
                    rightClassName="!w-auto"
                    rightSideContent={
                        <div className="flex flex-row gap-2">
                            <ButtonCmp type="button" className="btn_secondary_black" onClick={() => setIsShowImportModal(true)}>
                                <div className="flex items-center gap-1">
                                    <Upload01 className="w-4 h-4" />
                                    {t('AddressBook.Import_Addresses')}
                                </div>
                            </ButtonCmp>
                            <ButtonCmp type="button" className="btn-outline-primary" onClick={() => setIsShowAddAddressModal(true)}>
                                <div className="flex items-center gap-1">
                                    <Plus className="text-primary w-4 h-4" />
                                    {t('AddressBook.Add_New_Address')}
                                </div>
                            </ButtonCmp>
                        </div>
                    }
                />
            }
        >
            <div className="table-box-wrap w-full relative">
                <div className="table-bottom-header table-header-filter-space justify-between !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                    <div className={`flex items-center gap-x-1 flex-1 overflow-hidden ${isRowSortable.isOpen ? 'opacity-60 pointer-events-none !cursor-default' : ''}`}>
                        <TableTabButton
                            tabArray={tabsData}
                            tabParentClassName="whitespace-nowrap scrollbar-hide"
                            isActive={activeTab}
                            isTabDataInClick={true}
                            isShowAction={false}
                            tabTextClass="!max-w-[104px] truncate"
                            handleOnClick={handleTabClick}
                            isSkeletonLoading={isTabsLoading}
                            skeletonCount={3}
                        />
                    </div>

                    {!isRowSortable.isOpen ? (
                        <div className="flex items-center gap-x-2">
                            <InputText
                                inputName="searchAddress"
                                size="sm"
                                placeholder={t('Common.Search')}
                                className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                                icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                                value={search}
                                inputType="text"
                                isClearable={true}
                                onChangeFunc={handleFilter}
                                parentClassName="table-searchInput"
                            />

                            <TabButton
                                isTab={true}
                                isOnlyIcon={true}
                                tabSize="sm"
                                tabArray={[
                                    { value: 'grid', name: '', icon: <Grid01 className="w-4 h-4 pointer-events-none" /> },
                                    { value: 'list', name: '', icon: <List className="w-4 h-4 pointer-events-none" /> },
                                ]}
                                isActive={viewMode}
                                handleOnClick={(e: any) => {
                                    const value = e?.target?.dataset?.value || e?.currentTarget?.dataset?.value;
                                    if (value) setViewMode(value as 'grid' | 'list');
                                }}
                            />
                            <ButtonCmp
                                className="btn_secondary_black flex-none"
                                onClick={handleSortable()}
                                size="square-sm"
                                disabled={addresses.length === 0 || viewMode === 'grid'}
                                icon={<Columns03 className="w-4 h-4" />}
                            >
                                <></>
                            </ButtonCmp>
                            <div className="table-recordsPerPage flex-none">
                                <SelectBox
                                    SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                    selectLabelWrapClass="table-recordsPerPageSelectValue"
                                    name="recordsPerPageGroup"
                                    id="recordsPerPageGroup"
                                    className="form_control shadow"
                                    size="sm"
                                    options={recordsPerPageArray}
                                    onChangeFunc={(event: any) => {
                                        setParams((old: any) => {
                                            if (old.limit === event.value) return old;

                                            return { ...old, limit: event.value, page: 1 };
                                        });
                                    }}
                                    isSearchable={false}
                                    value={recordsPerPageArray.find((val: any) => val.value === params.limit)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-x-2">
                            <ButtonCmp type="button" onClick={handleSortable(false)} className="btn_secondary_black">
                                {t('Common.Cancel')}
                            </ButtonCmp>
                            <ButtonCmp type="button" onClick={handleColumnOrderSubmit} disabled={!hasColumnChanges} className="btn-outline-primary">
                                {t('Common.Save')}
                            </ButtonCmp>
                        </div>
                    )}
                </div>

                <div className={`draggable-layout-view ${isRowSortable.isOpen ? 'slide-in' : 'slide-out'}`}>
                    <DraggableLayout sortableData={isRowSortable?.data} renderCellContent={AddressDraggableCell} onColumnChange={handleColumnChange} />
                </div>

                <div className={`w-full table-box-container draggable-layout-view ${isRowSortable.isOpen ? 'slide-out' : 'slide-in'}`}>
                    {viewMode === 'list' ? (
                        <>
                            {bulkSelection.isOpen && (
                                <BulkActionBar
                                    selectedRows={selectedAddressIds as any}
                                    setSelectedRows={setSelectedAddressIds as any}
                                    allIds={addresses.map((a: IAddressListRow) => a.id) as any}
                                    showOnlySelected={showAllSelected}
                                    onShowOnlySelectedChange={setShowAllSelected}
                                    toolbar={<AddressBulkActionBar />}
                                />
                            )}
                            <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                                <TableCmp
                                    headCells={headCells}
                                    tableDataArr={displayedAddresses}
                                    TableLoaderRowCmp={AddressLoaderRow}
                                    isBulkBarOpen={bulkSelection.isOpen}
                                    TableRowCmp={AddressListRow}
                                    tableRowCmpProps={{
                                        onEdit: handleEdit,
                                        onDelete: (address: any) => handleActionType('team', 'address-delete', address)(),
                                        getAdditionalServices,
                                        getAddressTypes,
                                        countryFlagFn: getCountryFlag,
                                        headCells,
                                        selectedAddressIds,
                                        onToggleSelect: handleToggleSelectAddress,
                                    }}
                                    params={params}
                                    setParams={setParams}
                                    tableRowHeight="xs"
                                    tableBodyRowClass="bg-white"
                                    isFirstColumnSticky
                                    isActionColumnSticky
                                    isTableDataLoading={isLoading}
                                    numberOfSkeletonRows={25}
                                />
                            </div>
                        </>
                    ) : (
                        <AddressGridView
                            isLoading={isLoading}
                            addresses={addresses}
                            getAddressTypes={getAddressTypes}
                            getAdditionalServices={getAdditionalServices}
                            onEdit={handleEdit}
                            onDelete={(address: any) => handleActionType('team', 'address-delete', address)()}
                            t={t}
                        />
                    )}

                    {!isLoading && !addresses.length && (
                        <NotFoundUI
                            title={search ? t('AddressBook.No_Addresses_Found') : t('AddressBook.No_Addresses_Found')}
                            desc={search ? t('AddressBook.No_Addresses_Found_Desc') : t('AddressBook.No_Addresses_Found_Desc')}
                            containerClassName="!h-[auto] flex-1"
                            iconClassName="!hidden"
                            iconSecondaryClassName="!flex"
                            isEmptyBackground={true}
                            iconSecondary={search ? <SearchLg className="w-5 h-5 text-primary" /> : <AlertTriangle className="w-5 h-5 text-primary" />}
                        />
                    )}
                </div>

                {!isRowSortable.isOpen && (
                    <div className="w-full bg-white rounded-b-xl">
                        <CustomPagination
                            recordsPerPage={params.limit}
                            totalRecords={total}
                            currentPage={params.page}
                            handlePagination={(page: number) => {
                                setParams((old: any) => ({ ...old, page }));
                            }}
                        />
                    </div>
                )}
            </div>

            {isShowImportModal && (
                <ImportAddressModal
                    handleClose={(didImport: boolean) => {
                        setIsShowImportModal(false);

                        if (didImport) {
                            fetchAddresses();
                        }
                    }}
                />
            )}
            {isShowAddAddressModal && (
                <AddAddressModal
                    handleClose={handleModalClose}
                    additionalServices={additionalServices}
                    onSubmit={handleAddressModalSubmit}
                    addressData={editAddressData}
                    setAddressData={(data: any) => {
                        if (data) {
                            setEditAddressData({ ...editAddressData, ...data });
                        }

                        return {};
                    }}
                />
            )}

            {action.mode === 'address-delete' && (
                <DeleteModal
                    moduleName={t('AddressBook.Address')}
                    moduleDeleteModalContent={t('AddressBook.Delete_Address_Confirmation')}
                    handleClose={handleModalAction}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isDeleteLoading}
                    primaryBtnText=""
                />
            )}
        </PageSectionLayout>
    );
};

export default AddressBook;
